// Edge lines and contact shading, in the material a tile already draws
// with (owner, 2026-09-19: "done in the SHADER, not with extra geometry,
// because memory is the constraint").
//
// Both effects read what the fragment already knows - the view-space
// normal and where the fragment sits - and their screen-space
// derivatives, which a GPU computes for free from the 2x2 quad it is
// already shading. So:
//   - no edge geometry (SpaceX's 69M triangles would need a line list of
//     its own),
//   - no second draw of the scene, no depth or normal target, no G-buffer,
//   - nothing per element, so instancing, batching, the merged meshes,
//     the ghost halftone and every tint keep working untouched.
//
// EDGES. Two things read as an edge on a model:
//   - the normal turns a corner between one pixel and the next (a box's
//     own edges - the faces either side have different normals), and
//   - the surface breaks: the fragment's depth jumps by much more than
//     the surface's own slope predicts, which is a silhouette against
//     whatever is behind it.
// Both are measured per pixel, so an edge stays one pixel wide however
// close the camera is, and nothing needs to know the model's scale.
//
// CONTACT SHADE. Where a surface curves INTO itself - the inside of a
// corner, the line where a wall meets a floor, the fold of a duct - less
// of the room can reach it. Curvature is the same derivative pair read
// for its sign: the normal turning away from the direction of travel is
// concave, and concave gets darker. It is a local, honest approximation
// of ambient occlusion, not a screen-space AO with a depth buffer - it
// darkens creases, and cannot know that a distant column shades a wall.
//
// Both are one multiply on the final colour, so a tint, the halftone and
// the ghost shell all keep their look and only get darker in the creases.
//
// The strengths are ONE uniform object shared by every patched material,
// so turning an effect on or off is a value write and a redraw - never a
// shader recompile, which on SpaceX would hitch for seconds.
import { markSceneChanged } from './renderSignal';

// 0 = off. The shader is compiled once with both terms in it; at 0 they
// multiply by exactly 1.0, which is why the toggle is free.
export const shadingUniforms = {
  uBlEdge: { value: 0 },
  uBlAo: { value: 0 },
  // Where an edge starts to count, as a normal turn per pixel. Low picks
  // up gentle curvature - which on a round conduit darkens the whole
  // member, not just its outline - and high keeps to sharp corners and
  // silhouettes. A uniform rather than a constant so the two can be
  // compared on a real model, in a real browser, without a rebuild:
  //   viewer.setShading({ edgeSharpness: 0.6 })
  uBlEdgeSharp: { value: 0.25 },
  // Not a uniform: the strength contact shading is turned ON at.
  contactStrength: null,
};

export const EDGE_STRENGTH = 0.85;   // how black an edge pixel goes
export const AO_STRENGTH = 0.55;     // how dark the deepest crease goes

const VERT_DECL = 'varying vec3 vBlViewPos;\n';
// After project_vertex, `mvPosition` is this vertex in view space, with
// instancing, batching and skinning already applied - the one place that
// is true for every path the viewer draws.
const VERT_ASSIGN = 'vBlViewPos = mvPosition.xyz;\n';

const FRAG_DECL = `
uniform float uBlEdge;
uniform float uBlAo;
uniform float uBlEdgeSharp;
varying vec3 vBlViewPos;
float blShade = 1.0;
`;

// The body is the same wherever the normal comes from. A LIT material
// (MeshStandard / Lambert / Phong) hands us `normal` in view space; an
// UNLIT one - MeshBasicMaterial, which the merged-tile path falls back
// to when a part arrives without a material - has no normal in its
// fragment shader at all, and the first version of this simply refused
// to patch it. That is why the owner could turn everything on and see
// flat colour (2026-09-20): on a big model much of what he was looking
// at had no normal to read. For those, the normal is the cross product
// of the view position's own screen derivatives, which is exactly the
// face normal of whatever triangle covers the pixel.
const shadeBody = (normalExpr) => `
if ( uBlEdge > 0.0 || uBlAo > 0.0 ) {
  vec3 blN = ${normalExpr};
  vec3 blDnx = dFdx( blN );
  vec3 blDny = dFdy( blN );
  vec3 blP = vBlViewPos;
  vec3 blDpx = dFdx( blP );
  vec3 blDpy = dFdy( blP );
  float blDist = max( -blP.z, 1e-3 );

  // A corner: how far the normal turns from this pixel to the next.
  float blTurn = sqrt( dot( blDnx, blDnx ) + dot( blDny, blDny ) );

  // A break: the depth step the surface's own slope predicts, against
  // the step there actually is. A floor seen at a grazing angle steps a
  // long way per pixel and is NOT an edge, which is what the prediction
  // is for.
  float blNz = abs( blN.z ) > 0.05 ? blN.z : sign( blN.z + 1e-6 ) * 0.05;
  float blPredX = -( blN.x * blDpx.x + blN.y * blDpx.y ) / blNz;
  float blPredY = -( blN.x * blDpy.x + blN.y * blDpy.y ) / blNz;
  float blBreak = ( abs( blDpx.z - blPredX ) + abs( blDpy.z - blPredY ) ) / blDist;

  float blEdge = max( smoothstep( uBlEdgeSharp, uBlEdgeSharp + 0.85, blTurn ), smoothstep( 0.004, 0.02, blBreak ) );

  // Curvature, signed: the normal turning against the direction of
  // travel is the inside of a fold.
  float blLenX = max( length( blDpx ), 1e-6 );
  float blLenY = max( length( blDpy ), 1e-6 );
  float blCurve = -( dot( blDnx, blDpx ) / blLenX + dot( blDny, blDpy ) / blLenY );
  float blCrease = clamp( blCurve * 1.6, 0.0, 1.0 );

  blShade = ( 1.0 - uBlEdge * blEdge ) * ( 1.0 - uBlAo * blCrease );
}
`;

// With a normal: read it where the material computed it.
const FRAG_SHADE = shadeBody('normalize( normal )');
// Without one: the face normal of the triangle under the pixel. Flat by
// nature, which is right - an unlit material is drawing flat faces.
const FRAG_SHADE_FLAT = shadeBody('normalize( cross( dFdx( vBlViewPos ), dFdy( vBlViewPos ) ) )');

const FRAG_APPLY = 'gl_FragColor.rgb *= blShade;\n';

// What the patch needs: somewhere to put the view position in the vertex
// shader, and the last line of the fragment shader to multiply. The
// NORMAL is optional - see FRAG_SHADE_FLAT.
function canPatch(shader) {
  return typeof shader.fragmentShader === 'string'
    && shader.fragmentShader.includes('#include <dithering_fragment>')
    && typeof shader.vertexShader === 'string'
    && shader.vertexShader.includes('#include <project_vertex>');
}
const hasNormal = (shader) => shader.fragmentShader.includes('#include <normal_fragment_begin>');

// Patched once per material, chaining whatever patch is already on it
// (the ghost discard, the batch tint, the merged-tile state).
export function applySurfaceShading(material) {
  if (!material) return material;
  material.userData = material.userData || {};
  // The guard is on the FUNCTION, not on a userData flag, and that is the
  // whole bug the owner reported on 2026-09-20. three's Material.copy()
  // deep-copies userData but does NOT copy onBeforeCompile or
  // customProgramCacheKey - so `group.material.clone()` in tileBatch gave
  // a material that CLAIMED to be patched (the flag came across) and
  // carried none of the patch. It returned early here, the batch drew
  // with an unpatched program, and every tile drawn as a batch ignored
  // the effects while the pools - which share the original, genuinely
  // patched material - obeyed them. That is exactly "flat everywhere
  // except one fragment, and not its other instances".
  if (typeof material.onBeforeCompile === 'function' && material.onBeforeCompile.__blShading) return material;

  const previous = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    if (typeof previous === 'function') previous(shader, renderer);
    if (!canPatch(shader)) return;
    shader.uniforms.uBlEdge = shadingUniforms.uBlEdge;
    shader.uniforms.uBlAo = shadingUniforms.uBlAo;
    shader.uniforms.uBlEdgeSharp = shadingUniforms.uBlEdgeSharp;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + VERT_DECL)
      .replace('#include <project_vertex>', '#include <project_vertex>\n' + VERT_ASSIGN);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + FRAG_DECL)
      .replace(
        hasNormal(shader) ? '#include <normal_fragment_begin>' : '#include <dithering_fragment>',
        hasNormal(shader)
          ? '#include <normal_fragment_begin>\n' + FRAG_SHADE
          : FRAG_SHADE_FLAT + FRAG_APPLY + '#include <dithering_fragment>',
      );
    if (hasNormal(shader)) {
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <dithering_fragment>', FRAG_APPLY + '#include <dithering_fragment>');
    }
  };
  // Tag the installed function, so a CLONE - which does not inherit it -
  // is patched rather than skipped, and a re-patch of the same material
  // is still a no-op. userData keeps the flag for diagnostics.
  material.onBeforeCompile.__blShading = true;
  material.userData.__blShading = true;

  const prevKey = typeof material.customProgramCacheKey === 'function'
    ? material.customProgramCacheKey.bind(material) : null;
  material.customProgramCacheKey = () => (prevKey ? prevKey() : '') + '|blShade2';
  material.needsUpdate = true;
  return material;
}

// -> { edges, contact } as they now stand.
export function surfaceShadingState() {
  return {
    edges: shadingUniforms.uBlEdge.value > 0,
    contact: shadingUniforms.uBlAo.value > 0,
    edgeSharpness: shadingUniforms.uBlEdgeSharp.value,
    contactStrength: shadingUniforms.contactStrength != null ? shadingUniforms.contactStrength : AO_STRENGTH,
  };
}

// The only way the app turns them on and off. A value write on the shared
// uniforms plus one redraw - no material is touched, nothing recompiles.
// 0..1, how dark the deepest crease goes. Until now this was fixed at
// AO_STRENGTH and the owner's first impression of the composite was
// "everything got muddy" (leader, 2026-09-20), which is a reason to give
// the strength to the person rather than to argue about the constant.
export function setSurfaceShading({ edges, contact, edgeSharpness, contactStrength } = {}) {
  let changed = false;
  if (edges !== undefined) {
    const v = edges ? EDGE_STRENGTH : 0;
    if (shadingUniforms.uBlEdge.value !== v) { shadingUniforms.uBlEdge.value = v; changed = true; }
  }
  if (contactStrength !== undefined) {
    const s = Math.max(0, Math.min(1, Number(contactStrength) || 0));
    shadingUniforms.contactStrength = s;
    // Only takes effect while contact shading is on; turning it on later
    // picks the new strength up.
    if (shadingUniforms.uBlAo.value > 0 && shadingUniforms.uBlAo.value !== s) { shadingUniforms.uBlAo.value = s; changed = true; }
  }
  if (contact !== undefined) {
    const v = contact ? (shadingUniforms.contactStrength != null ? shadingUniforms.contactStrength : AO_STRENGTH) : 0;
    if (shadingUniforms.uBlAo.value !== v) { shadingUniforms.uBlAo.value = v; changed = true; }
  }
  if (edgeSharpness !== undefined) {
    const v = Math.max(0.05, Math.min(1.5, Number(edgeSharpness) || 0));
    if (shadingUniforms.uBlEdgeSharp.value !== v) { shadingUniforms.uBlEdgeSharp.value = v; changed = true; }
  }
  // Render on demand: without this the canvas keeps the old picture until
  // something else happens to ask for a frame.
  if (changed) markSceneChanged('shading');
  return changed;
}
